import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DEFAULT_PROMO_CONFIG, usePromoConfig } from "@/hooks/use-promo-config";
import { toast } from "sonner";

export function PromoControls() {
  const { config: promoConfig, updateConfig: updatePromoConfig, resetConfig: resetPromoConfig } = usePromoConfig();
  const [promoDraft, setPromoDraft] = useState({
    enabled: promoConfig.enabled,
    bannerText: promoConfig.bannerText,
    ctaLabel: promoConfig.ctaLabel,
    ctaHref: promoConfig.ctaHref,
  });

  useEffect(() => {
    setPromoDraft({
      enabled: promoConfig.enabled,
      bannerText: promoConfig.bannerText,
      ctaLabel: promoConfig.ctaLabel,
      ctaHref: promoConfig.ctaHref,
    });
  }, [promoConfig]);

  const handlePromoSave = () => {
    updatePromoConfig({
      enabled: promoDraft.enabled,
      bannerText: promoDraft.bannerText.trim() || DEFAULT_PROMO_CONFIG.bannerText,
      ctaLabel: promoDraft.ctaLabel.trim() || DEFAULT_PROMO_CONFIG.ctaLabel,
      ctaHref: promoDraft.ctaHref.trim() || DEFAULT_PROMO_CONFIG.ctaHref,
    });
    toast.success("Banner settings updated.");
  };

  const handlePromoReset = () => {
    resetPromoConfig();
    toast.success("Promo settings reset.");
  };

  return (
    <Card className="cyber-card w-full mx-auto">
      <CardHeader className="flex flex-col gap-2">
        <CardTitle>Promotion Banner</CardTitle>
        <CardDescription>
          Update the banner text and CTA shown across the marketing site.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-border/50 bg-muted/20 p-4">
            <div>
              <Label htmlFor="promo-enabled">Banner active</Label>
              <p className="text-xs text-muted-foreground">Show the top banner site-wide.</p>
            </div>
            <Switch
              id="promo-enabled"
              checked={promoDraft.enabled}
              onCheckedChange={(checked) => setPromoDraft((prev) => ({ ...prev, enabled: checked }))}
            />
          </div>
        </div>
        <Accordion
          type="single"
          collapsible
          value={promoDraft.enabled ? "banner-settings" : undefined}
        >
          <AccordionItem value="banner-settings" className="border border-border/50 rounded-lg px-4">
            <AccordionTrigger>
              Banner settings
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="promo-banner-text">Banner text</Label>
                  <Input
                    id="promo-banner-text"
                    value={promoDraft.bannerText}
                    onChange={(e) => setPromoDraft((prev) => ({ ...prev, bannerText: e.target.value }))}
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="promo-cta">CTA label</Label>
                    <Input
                      id="promo-cta"
                      value={promoDraft.ctaLabel}
                      onChange={(e) => setPromoDraft((prev) => ({ ...prev, ctaLabel: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="promo-cta-href">CTA link</Label>
                    <Input
                      id="promo-cta-href"
                      value={promoDraft.ctaHref}
                      onChange={(e) => setPromoDraft((prev) => ({ ...prev, ctaHref: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handlePromoSave}>Save Banner Settings</Button>
          <Button variant="outline" onClick={handlePromoReset}>Reset Defaults</Button>
        </div>
      </CardContent>
    </Card>
  );
}
